import React from 'react';
import noop from 'lodash/noop';

import { DateFormat, formatDate } from '@lyearn/core/utils/date/formatDate';
import getDuration from '@lyearn/core/utils/date/getDuration';
import { useTranslation } from '@lyearn/i18n';
import { CheckMark, RepeatOutline } from '@lyearn/icons';
import { cn } from '@lyearn/style';
import { Checkbox, Dot, Stack, StatusPill, Tooltip, Typography } from '@lyearn/ui';
import { Image } from '@/components/Image';
import { getDescString } from '@/modules/1on1s/views/1on1Details/components/Header/utils';
import { ProviderOverrides } from '@/modules/content/modules/external-content/utils';
import { getIsRecurringSession } from '@/modules/events/utils';
import { AssignmentTypeEnum, EventStatusEnum, EventUnion } from '@/types/schema';
import { grayScale } from '@/utils/styles';

import { getEventInstructorLabel } from '../../../../events/views/EventManager/components/ListItemContainer/ListItemContent';
import { getUsersName } from '../../../../events/views/EventManager/utils';
import AssignmentType from './AssignmentType';

const PROVIDER_OVERRIDES: ProviderOverrides = {
  Icon: {
    className: 'w-16 h-16 rounded-4',
    width: 16,
    height: 16,
  },
  Label: {
    variant: 'label-02',
    color: 'text-text-secondary',
    className: 'ms-6 normal-case',
  },
};

function SessionSelector({
  event,
  isSelected,
  toggle,
  isAlreadySelected,
  multiSelect,
  showDuration,
  assignmentType,
  className,
  isAssingmentTypeChangeAllowed,
}: {
  event: EventUnion;
  isSelected: boolean;
  toggle: (eventId: string, assignmentType?: AssignmentTypeEnum) => void;
  isAlreadySelected?: boolean;
  multiSelect?: boolean;
  showDuration?: boolean;
  assignmentType?: AssignmentTypeEnum;
  className?: string;
  isAssingmentTypeChangeAllowed?: boolean;
}) {
  const { t } = useTranslation('Events');
  const { id, name, cover, startTime, endTime, status, instructors } = event;
  const instructorsLabel = getUsersName(t, instructors);

  const isRecurringSession = getIsRecurringSession(event.recurringCategory);

  const recurrenceScheduleLabel = event.recurrenceSchedule
    ? getDescString(t, event.recurrenceSchedule)
    : null;

  return (
    <Stack
      alignItems="center"
      className={cn(
        'flex-shrink-0 cursor-pointer border-b border-solid border-border py-16 pe-20 ps-16',
        className,
      )}
      justifyContent="between"
      onClick={() => (isAlreadySelected ? noop : toggle(id))} // flex-shrink-0: safari fix - ui collapsing in some safari versions
    >
      <Stack className="flex-shrink-1 truncate">
        <Stack className="flex-shrink-0 truncate">
          <Image
            alt={name}
            className={cn(
              'h-48 w-48 rounded-4',
              event.status === EventStatusEnum.Completed ? grayScale : undefined,
            )}
            height={48}
            src={cover?.url ?? ''}
            width={48}
          />
        </Stack>
        <Stack className="truncate ms-16" direction="col" gapY={4}>
          <Stack alignItems="center" className="me-16" gapX={8}>
            <Typography className="truncate" title={name || ''} variant="label-01">
              {name}
            </Typography>
            {isRecurringSession ? (
              <Tooltip title={t('Events:RecurringSession.RecurringSession')}>
                <RepeatOutline className="h-16 w-16 text-icon-placeholder hover:text-icon-secondary" />
              </Tooltip>
            ) : null}
            {status === EventStatusEnum.Completed ? (
              <StatusPill
                appearance="secondary"
                className="flex-shrink-0"
                label={t('Common:Filter.Finished')}
                size="medium"
                variant="light"
              />
            ) : null}
          </Stack>
          <Typography className="truncate" color="text-text-secondary" variant="body-short-02">
            <Stack alignItems="center">
              <Typography color="text-text-secondary" variant="body-short-02">
                {getEventInstructorLabel(event, isRecurringSession ? undefined : instructorsLabel)}
              </Typography>

              {isRecurringSession ? (
                <>
                  <Dot />
                  {recurrenceScheduleLabel}
                </>
              ) : null}

              {showDuration ? (
                <>
                  <Dot />
                  {getDuration(startTime, endTime)}
                </>
              ) : !isRecurringSession ? (
                <>
                  <Dot />
                  {formatDate(startTime, DateFormat.DateTime)}
                </>
              ) : null}
            </Stack>
          </Typography>
        </Stack>
      </Stack>
      <Stack alignItems="center" gapX={4}>
        {isAssingmentTypeChangeAllowed && isRecurringSession ? (
          <AssignmentType assignmentType={assignmentType} onChange={(val) => toggle(id, val)} />
        ) : null}
        {multiSelect ? (
          <Checkbox
            checked={isSelected || isAlreadySelected}
            data-attr="SELECT"
            data-id={id}
            disabled={isAlreadySelected}
          />
        ) : null}
        {!multiSelect && isSelected ? <CheckMark className="h-24 w-24" /> : null}
      </Stack>
    </Stack>
  );
}

export default SessionSelector;
